import { useEffect } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import Select from 'react-select';
import { useSelector } from 'react-redux';

import FileInput from '@/components/common/FileInput';

import { ReactComponent as RiDeleteBinLine } from '@/assets/icons/ri-delete-bin-line.svg';
import Toggle from '@/components/common/Toggle';
import { RootState, useAppDispatch } from '@/store';
import { getUserVenues } from '@/store/user-info/action';
import { uploadFiles } from '@/store/file/action';

const acceptedFileTypes = ['image/png', 'image/jpg', 'image/jpeg'];
const MAX_FILE_SIZE = 1054294 * 2;

const getFileUrl = (fileInput: File) => URL.createObjectURL(fileInput);

const getFileSize = (bytes, decimals = 2) => {
	if (!+bytes) return '0 Bytes';

	const k = 1024;
	const dm = decimals < 0 ? 0 : decimals;
	const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];

	const i = Math.floor(Math.log(bytes) / Math.log(k));

	return `${parseFloat((bytes / k ** i).toFixed(dm))} ${sizes[i]}`;
};

const ThumbnailSection = ({ file, setFile }) => {
	const form = useFormContext();
	const dispatch = useAppDispatch();

	const setFileFromResponse = (response) => {
		const { payload } = response;
		form.setValue('thumbnailLink', payload.url);
	};

	const handleChangeFile = (fileInput?: File) => {
		if (fileInput && fileInput.size < MAX_FILE_SIZE) {
			const formData = new FormData();
			formData.append('file', fileInput);
			dispatch(uploadFiles({ formData })).then(setFileFromResponse);
			setFile(fileInput);
		}
	};

	return (
		<>
			<div className="text-jsm">Thumbnail</div>
			<FileInput
				classNames="mt-2 min-h-[200px]"
				setFile={handleChangeFile}
				accept={acceptedFileTypes}
			/>
			{file && (
				<div className="flex gap-2 mt-2">
					<div className="rounded overflow-hidden">
						<img
							src={getFileUrl(file)}
							alt=""
							className="h-[50px] w-[100px] object-cover"
						/>
					</div>
					<div className="flex flex-grow justify-between items-center">
						<div className="flex flex-col gap-2">
							<div className="text-jsm font-medium">{file.name}</div>
							<div className="text-jsm font-normal text-grey-dark">
								{getFileSize(file.size)}
							</div>
						</div>
						<button
							onClick={() => setFile(null)}
							type="button"
							className="text-red-primary"
						>
							<RiDeleteBinLine />
						</button>
					</div>
				</div>
			)}
		</>
	);
};

const VenueSection = ({ venues }) => {
	const form = useFormContext();
	return (
		<div className="flex flex-col">
			<div className="text-jsm font-medium mb-1">Venue</div>
			<div className="text-jxs text-grey-dark mb-4">
				Which venue is this news published?
			</div>

			<Controller
				name="venueId"
				control={form.control}
				render={() => (
					<Select
						options={venues.map((venue) => ({
							label: venue.name,
							value: venue.id,
						}))}
						placeholder="Select venue"
						value={{
							value: form.watch('venueId'),
							label: form.watch('venueName'),
						}}
						onChange={(val) => {
							form.setValue('venueId', val?.value);
							form.setValue('venueName', val?.label);
						}}
						isClearable
					/>
				)}
				rules={{ required: true }}
			/>
		</div>
	);
};

const CategorySection = () => {
	const { register, watch } = useFormContext();

	const category = watch('category');

	return (
		<div className="flex flex-col">
			<div className="text-jsm font-medium mb-1">Category</div>
			<div className="text-jxs text-grey-dark mb-4">
				Select the relevant category
			</div>
			<div className="flex items-center mb-4">
				<input
					{...register('category')}
					checked={category === 'event'}
					type="radio"
					value="event"
					className="w-4 h-4"
				/>
				<label
					htmlFor="default-radio-1"
					className="ml-2 text-sm font-medium text-gray-900 dark:text-gray-300"
				>
					Event
				</label>
			</div>
			<div className="flex items-center">
				<input
					{...register('category')}
					checked={category === 'announcement'}
					type="radio"
					value="announcement"
					className="w-4 h-4"
				/>
				<label
					htmlFor="default-radio-2"
					className="ml-2 text-sm font-medium text-gray-900 dark:text-gray-300"
				>
					Announcement
				</label>
			</div>
		</div>
	);
};

const ShowSection = () => {
	const form = useFormContext();
	return (
		<div className="flex flex-col">
			<div className="text-jsm font-medium mb-1">Tampilkan pada beranda</div>
			<div className="text-jxs text-grey-dark mb-4">
				Switch jika ingin menampilkan pada beranda
			</div>
			<Toggle
				onComponent={<span className="ml-2 text-sm font-medium">Tampil</span>}
				offComponent={
					<span className="ml-2 text-sm font-medium text-grey-soft">
						Tidak Tampil
					</span>
				}
				value={form.watch('showOnHome')}
				onToggle={(v) => form.setValue('showOnHome', v)}
			/>
		</div>
	);
};

const RightSection = ({ file, setFile }: RightSectionProps) => {
	const dispatch = useAppDispatch();
	const venueState = useSelector(
		(state: RootState) => state.userInfo.state.venues
	);

	useEffect(() => {
		(async () => {
			await dispatch(getUserVenues({}));
		})();
	}, []);

	return (
		<>
			<div className="p-6">
				<ThumbnailSection file={file} setFile={setFile} />
			</div>
			<div className="p-6 border-t-2 border-grey-soft">
				<VenueSection venues={venueState} />
			</div>
			<div className="p-6 border-t-2 border-grey-soft">
				<CategorySection />
			</div>
			<div className="p-6 border-t-2 border-grey-soft">
				<ShowSection />
			</div>
		</>
	);
};

export default RightSection;
